import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, LogOut, Zap, Loader2, Sparkles, TrendingUp } from "lucide-react";
import ChatBubble from "../components/ChatBubble";
import OrderSheet from "../components/OrderSheet";
import { getSession, clearSession, parseQuoteBlock } from "../lib/session";
import { sendChatMessage, getChatHistory, emailMeThisQuote, requoteMulti, getProducts, getPapers, getSessionSavings } from "../lib/api";

const SUGGESTIONS = [
  "500 full-color business cards, double sided, 14pt gloss",
  "100 tri-fold brochures, 8.5x11, 100lb gloss text",
  "A 2x4 ft vinyl banner for a grand opening",
  "5 feet of DTF prints for t-shirt transfers",
];

const ChatPage = () => {
  const navigate = useNavigate();
  const session = useMemo(() => getSession(), []);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeQuote, setActiveQuote] = useState(null);
  const [productsById, setProductsById] = useState({});
  const [papersById, setPapersById] = useState({});
  const [savings, setSavings] = useState(null);
  const scrollRef = useRef(null);

  const refreshSavings = async () => {
    if (!session?.session_id) return;
    try {
      const data = await getSessionSavings(session.session_id);
      setSavings(data);
    } catch {
      /* non-blocking — header just won't show savings */
    }
  };

  useEffect(() => {
    if (!session?.session_id) {
      navigate("/");
      return;
    }
    (async () => {
      try {
        const data = await getChatHistory(session.session_id);
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages);
        } else {
          // Seed welcome message on the client (we do not persist until the user sends)
          setMessages([
            {
              id: "welcome",
              role: "assistant",
              content:
                "Hi! I'm your Roswell Print Center quote assistant. Tell me what you'd like to print today — include product type, quantity, size, color, paper, and any finishing. I'll calculate retail pricing and apply your partner discount automatically.",
              created_at: new Date().toISOString(),
            },
          ]);
        }
      } catch {
        toast.error("Failed to load chat history");
      } finally {
        setLoadingHistory(false);
      }
    })();
    // Fetch the catalog once for line-edit dropdowns
    (async () => {
      try {
        const [products, papers] = await Promise.all([getProducts(), getPapers()]);
        const pById = {};
        products.forEach((p) => { pById[p.id] = p; });
        const paById = {};
        papers.forEach((pp) => { paById[pp.id] = pp; });
        setProductsById(pById);
        setPapersById(paById);
      } catch {
        /* non-blocking — Edit will still work for quantity-only changes */
      }
    })();
    // Pull YTD savings for the chat header
    refreshSavings();
  }, [session, navigate]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const chatTranscript = useMemo(() => {
    return messages
      .filter((m) => m.id !== "welcome")
      .map((m) => `${m.role === "user" ? "Customer" : "UPS Print Hub Bot"}: ${m.content}`)
      .join("\n\n");
  }, [messages]);

  const lastAssistantQuote = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") {
        const q = parseQuoteBlock(messages[i].content);
        if (q) return q;
      }
    }
    return null;
  }, [messages]);

  // The most recent assistant message that actually contains a quote block.
  // Order Now / Email / Copy stay enabled on this card even if the user keeps
  // chatting and newer non-quote assistant messages arrive.
  const lastQuotedAssistantIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant" && parseQuoteBlock(messages[i].content)) return i;
    }
    return -1;
  }, [messages]);

  // The most recent assistant message overall — used by the in-chat material
  // picker so we only show pick buttons on the latest turn (otherwise older
  // pickers stick around and confuse the customer once they've already moved
  // past that decision point).
  const lastAssistantIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return i;
    }
    return -1;
  }, [messages]);

  const handleSend = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput("");
    const tempUser = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev.filter((m) => m.id !== "welcome"), tempUser]);
    setSending(true);
    try {
      const res = await sendChatMessage(session.session_id, content);
      setMessages((prev) => {
        const cleaned = prev.filter((m) => m.id !== tempUser.id);
        return [...cleaned, res.user_message, res.assistant_message];
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send message";
      toast.error(msg);
      setMessages((prev) => prev.filter((m) => m.id !== tempUser.id));
      setInput(content);
    } finally {
      setSending(false);
    }
  };

  const [bigheadsByMessage, setBigheadsByMessage] = useState({});
  const [activeFatheadIds, setActiveFatheadIds] = useState([]);

  // When a customer clicks "Pick this" inside an in-chat material picker,
  // immediately send the choice back to the LLM as the next user message.
  // The LLM is waiting for the customer's material decision so the natural
  // turn is "I'll go with the 16pt Gloss Cover."
  const handlePickMaterial = (material) => {
    const label = material?.label;
    if (!label) return;
    handleSend(`I'd like to go with ${label}.`);
  };

  // Opening the full detail modal on a card is a non-blocking action — for now
  // we just pop the customer into the public Library page in a new tab so they
  // can read the full spec sheet. Could be replaced with an in-chat modal later.
  const handleOpenMaterial = (material) => {
    if (!material?.slug) return;
    window.open(`/materials#${material.slug}`, "_blank", "noopener,noreferrer");
  };

  // One-time hydration of the library_intent crumb left by the public Library
  // page → "Get a quote" CTA. The customer landed on /materials, tapped a
  // card's Get-a-quote button, and now hits the chat — we pre-fill their first
  // message so they don't have to retype the material name.
  useEffect(() => {
    if (loadingHistory) return;
    if (messages.length > 1) return; // they already started chatting
    try {
      const raw = sessionStorage.getItem("library_intent");
      if (!raw) return;
      const intent = JSON.parse(raw);
      sessionStorage.removeItem("library_intent");
      if (intent?.label) {
        setInput(`I'd like a quote on ${intent.label}.`);
      }
    } catch { /* malformed crumb — ignore */ }
  }, [loadingHistory, messages.length]);

  const handleOpenOrderSheet = ({ quote, fatheadIds }) => {
    if (!quote) {
      toast.error("No quote to order yet. Ask the bot for a price first.");
      return;
    }
    setActiveQuote(quote);
    setActiveFatheadIds(fatheadIds || []);
    setSheetOpen(true);
  };

  const handleBigheadsChange = (messageId, next) => {
    setBigheadsByMessage((prev) => ({ ...prev, [messageId]: next }));
  };

  const handleEmailQuote = async (quote, variant = "full") => {
    if (!session?.session_id || !quote) return false;
    try {
      const res = await emailMeThisQuote({
        sessionId: session.session_id,
        quote,
        recipientEmail: session.email,
        variant,
      });
      if (res?.email_status === "sent") {
        const label = variant === "retail" ? "Retail quote" : "Quote";
        toast.success(`${label} emailed to ${res.recipient}`);
        return true;
      }
      toast.message(`Quote recorded (${res?.email_status || "no email configured"})`);
      return true;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not email the quote";
      toast.error(msg);
      return false;
    }
  };

  const handleRequote = async ({ quote, lineIndex, patch }) => {
    if (!session?.session_id || !quote) return false;
    const lines = quote.lines || (quote.product_id ? [quote] : []);
    if (!lines.length) {
      toast.error("Cannot edit this quote.");
      return false;
    }
    // Build the items[] array, applying the patch only to the edited line.
    const items = lines.map((line, idx) => {
      const base = {
        product_id: line.product_id || "unknown",
        size_id: line.size_id || null,
        paper_id: line.paper_id || null,
        quantity: Number(line.quantity || 1),
        finish_ids: line.finish_ids || [],
        pages: line.pages || null,
        custom_size_requested: line.custom_size_requested || null,
        custom_paper_requested: line.custom_paper_requested || null,
        custom_width_in: line.custom_width_in || null,
        custom_height_in: line.custom_height_in || null,
        turnaround: line.turnaround || "standard (2 business days)",
        product_name: line.product_name || null,
        description: line.specifications || null,
      };
      if (idx === lineIndex) {
        if (patch.quantity != null) base.quantity = Number(patch.quantity);
        if (patch.paper_id) base.paper_id = patch.paper_id;
      }
      return base;
    });
    try {
      const res = await requoteMulti({
        sessionId: session.session_id,
        items,
        editedIndex: lineIndex,
      });
      if (res?.assistant_message) {
        setMessages((prev) => [...prev, res.assistant_message]);
        toast.success("Quote updated");
      }
      return true;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not update the quote";
      toast.error(msg);
      return false;
    }
  };

  const handleOrderSuccess = (result) => {
    setSheetOpen(false);
    navigate(`/order/success/${result.order_id}`, { state: { result, quote: activeQuote, email: session.email } });
  };

  const handleSignOut = () => {
    clearSession();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--ups-paper)" }}>
      {/* Header */}
      <header
        className="sticky top-0 z-20 border-b"
        style={{ background: "#fff", borderColor: "var(--ups-border)" }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/site-logo.webp"
              alt="Roswell · Your Printhub Partners"
              className="h-10 w-auto rounded-sm"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display text-sm font-bold">Quote Bot</span>
              <span className="text-[10px] font-mono text-[color:var(--ups-subtext)]">
                session · {session?.session_id?.slice(0, 8)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-[color:var(--ups-subtext)]">
              <Sparkles size={12} /> {session?.email}
            </div>
            <button
              onClick={handleSignOut}
              className="text-xs font-bold uppercase tracking-[0.15em] flex items-center gap-1.5 px-3 py-1.5 hover:bg-[color:var(--ups-muted)] rounded-sm transition"
              data-testid="sign-out-button"
            >
              <LogOut size={14} /> Exit
            </button>
          </div>
        </div>
      </header>

      {/* Main chat area */}
      <main className="flex-1 flex flex-col max-w-4xl w-full mx-auto px-4 sm:px-6 py-6">
        <div className="mb-5">
          <h1 className="font-display text-3xl sm:text-4xl font-black leading-none tracking-tight">
            What are we printing today?
          </h1>
          <p className="mt-2 text-sm text-[color:var(--ups-subtext)]">
            Describe your product. The bot will ask clarifying questions and return a quote with retail pricing and your partner discount.
          </p>
          {savings && (savings.lifetime_orders > 0 || savings.discount_percent) && (
            <div
              className="mt-4 px-4 py-3 rounded-sm flex flex-wrap items-center gap-x-6 gap-y-1.5"
              style={{ background: "var(--ups-muted)", border: "2px solid var(--ups-ink)" }}
              data-testid="savings-banner"
            >
              <div className="flex items-center gap-2">
                <TrendingUp size={16} style={{ color: "var(--ups-accent)" }} />
                <span className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--ups-ink)" }}>
                  {savings.discount_label || "Partner"} · {savings.discount_percent}% off
                </span>
              </div>
              {savings.ytd_orders > 0 ? (
                <div className="text-[13px] font-mono" data-testid="ytd-savings">
                  <span style={{ color: "var(--ups-subtext)" }}>You&apos;ve saved</span>{" "}
                  <span className="font-bold" style={{ color: "var(--ups-accent)" }}>
                    ${savings.ytd_savings.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>{" "}
                  <span style={{ color: "var(--ups-subtext)" }}>across {savings.ytd_orders} order{savings.ytd_orders === 1 ? "" : "s"} in {savings.year}</span>
                </div>
              ) : (
                <div className="text-[12px] font-mono" style={{ color: "var(--ups-subtext)" }} data-testid="savings-empty">
                  Your discount is active — savings will tally up here as you place orders.
                </div>
              )}
              {savings.lifetime_orders > savings.ytd_orders && (
                <div className="text-[11px] font-mono" style={{ color: "var(--ups-subtext)" }} data-testid="lifetime-savings">
                  Lifetime: ${savings.lifetime_savings.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · {savings.lifetime_orders} orders
                </div>
              )}
            </div>
          )}
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto pr-1"
          style={{ minHeight: "50vh" }}
          data-testid="chat-scroll-area"
        >
          {loadingHistory ? (
            <div className="flex items-center gap-2 text-sm text-[color:var(--ups-subtext)]">
              <Loader2 size={14} className="animate-spin" /> Loading session…
            </div>
          ) : (
            <>
              {messages.map((m, idx) => (
                <ChatBubble
                  key={m.id}
                  role={m.role}
                  content={m.content}
                  messageId={m.id}
                  onOrderNow={handleOpenOrderSheet}
                  onEmailQuote={handleEmailQuote}
                  onRequote={handleRequote}
                  productsById={productsById}
                  papersById={papersById}
                  isLastAssistant={idx === lastQuotedAssistantIndex}
                  isLatestAssistant={idx === lastAssistantIndex}
                  sessionId={session?.session_id}
                  bigheadsFatheads={bigheadsByMessage[m.id] || []}
                  onBigheadsChange={handleBigheadsChange}
                  onPickMaterial={handlePickMaterial}
                  onOpenMaterial={handleOpenMaterial}
                />
              ))}
              {sending && (
                <div className="flex justify-start mb-4">
                  <div className="card-swiss px-4 py-3 rounded-sm">
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Suggestions */}
        {messages.length <= 1 && !sending && (
          <div className="mt-3 mb-4">
            <div className="overline mb-2">Try one</div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => handleSend(s)}
                  className="text-left text-sm px-3 py-2 card-swiss rounded-sm hover:bg-[color:var(--ups-muted)] transition flex items-start gap-2 max-w-md"
                  data-testid="chat-suggestion"
                >
                  <Zap size={14} className="mt-0.5 shrink-0" style={{ color: "#FFB500" }} />
                  <span>{s}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form
          onSubmit={(e) => { e.preventDefault(); handleSend(); }}
          className="sticky bottom-0 mt-3"
        >
          <div
            className="flex items-end gap-2 p-2 rounded-sm"
            style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="e.g. 250 full-color flyers, 8.5x11, 100lb gloss, double sided"
              rows={1}
              disabled={sending}
              className="flex-1 resize-none px-3 py-2 text-[15px] bg-transparent outline-none max-h-36"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="btn-brutal px-4 py-2.5 rounded-sm flex items-center gap-2 text-sm"
              data-testid="send-message-button"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              <span className="hidden sm:inline">Send</span>
            </button>
          </div>
          <div className="flex items-center justify-between mt-2 text-[11px] font-mono text-[color:var(--ups-subtext)]">
            <span>Press Enter to send · Shift+Enter for new line</span>
            {lastAssistantQuote && (
              <button
                type="button"
                onClick={() => handleOpenOrderSheet({ quote: lastAssistantQuote })}
                className="font-bold uppercase tracking-[0.15em] text-[color:var(--ups-ink)] hover:text-[color:var(--ups-accent)]"
                data-testid="reopen-order-button"
              >
                → Order last quote
              </button>
            )}
          </div>
        </form>
      </main>

      <OrderSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        sessionId={session?.session_id}
        sessionEmail={session?.email}
        quote={activeQuote}
        fatheadIds={activeFatheadIds}
        chatTranscript={chatTranscript}
        onSuccess={handleOrderSuccess}
      />
    </div>
  );
};

export default ChatPage;
